import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { invalidateSedrCategoriesCache } from './sedrUi';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrRoomCategoryDialog(options: {
  roomId: string;
  mode: 'create' | 'rename';
  categoryId?: string;
  initialName?: string;
  onDone: () => void | Promise<void>;
}): void {
  const shell = createModalShell({
    titleKey: options.mode === 'create'
      ? 'sedr.settings.createCategoryTitle'
      : 'sedr.settings.renameCategoryTitle',
    titleId: 'sedr-room-category-dialog-title',
    helpTopic: dialogHelpTopic('sedrRoomCategory'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-room-category-form">
      <label>
        <span data-i18n="sedr.settings.categoryNameLabel"></span>
        <input type="text" name="name" maxlength="200" required autocomplete="off" />
      </label>
      <p class="error-text" id="sedr-room-category-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-room-category-form');
  submitButton.setAttribute(
    'data-i18n',
    options.mode === 'create' ? 'sedr.settings.createCategory' : 'sedr.settings.renameCategory',
  );

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-room-category-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-room-category-error') as HTMLParagraphElement;
  const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
  nameInput.value = options.initialName ?? '';
  let submitting = false;
  let lastErrorKey = 'status.error';

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  nameInput.focus();
  nameInput.select();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      const name = String(new FormData(form).get('name') ?? '').trim();
      try {
        if (options.mode === 'create') {
          await sedrApi.createCategory(options.roomId, { name });
        } else {
          if (!options.categoryId) {
            throw new Error('missing_category_id');
          }
          await sedrApi.patchCategory(options.roomId, options.categoryId, { name });
        }
        invalidateSedrCategoriesCache(options.roomId);
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onDone();
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
